/**
 * Localized template/service copy. For locale=ar, *_Ar fields that duplicate English (no Arabic letters)
 * are ignored so we can fall back to real Arabic on the parent orderTemplate or other fields.
 */

import type { OrderTemplate, AppointmentSpec, ServiceTemplate } from '@/services/orderApi'
import {
  getAppointmentSpecLabelArOverride,
  getOrderTemplateDescriptionArOverride,
  getOrderTemplateNameArOverride,
  getServiceTemplateDescriptionArOverride,
  getServiceTemplateNameArOverride,
} from '@/constants/orderCreationStep4ArOverrides'

export type Locale = 'en' | 'ar'

export function textContainsArabicScript(text: string | undefined | null): boolean {
  if (!text) return false
  return /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/.test(text)
}

/**
 * Use an *_Ar API value only if it actually contains Arabic. Many nested serviceTemplates set
 * descriptionAr/nameAr to the same English string as descriptionEn — those must be skipped.
 */
export function effectiveArabicLocalizedText(arValue: string | undefined | null): string {
  const t = (arValue ?? '').trim()
  if (!t) return ''
  return textContainsArabicScript(t) ? t : ''
}

interface WithLocalizedName {
  id?: string
  name: string
  nameEn?: string
  nameAr?: string
}

interface WithLocalizedDescription {
  id?: string
  description?: string
  descriptionEn?: string
  descriptionAr?: string
}

/** Step 3 list & general — Arabic uses real *_Ar or Arabic in `name`; else legacy `name`. */
export function getOrderTemplateName(template: OrderTemplate, locale: Locale): string {
  if (locale === 'ar') {
    const ar = effectiveArabicLocalizedText(template.nameAr)
    if (ar) return ar
    if (textContainsArabicScript(template.name)) return template.name
    return template.name
  }
  if (locale === 'en' && template.nameEn) return template.nameEn
  return template.name
}

/** Step 3 & general — prefers real Arabic in descriptionAr; ignores English wrongly stored in descriptionAr. */
export function getOrderTemplateDescription(template: OrderTemplate, locale: Locale): string {
  if (locale === 'ar') {
    const ar = effectiveArabicLocalizedText(template.descriptionAr)
    if (ar) return ar
    const raw = template.description?.trim() ?? ''
    if (raw && textContainsArabicScript(raw)) return template.description ?? ''
    return getOrderTemplateDescriptionArOverride(
      template.id,
      template.descriptionEn ?? template.description
    ) ?? ''
  }
  if (locale === 'en' && template.descriptionEn) return template.descriptionEn
  return template.description ?? ''
}

export function getOrderTemplateTitleSchedulingStep(template: OrderTemplate, locale: Locale): string {
  if (locale !== 'ar') {
    return getOrderTemplateName(template, locale)
  }
  const ar = effectiveArabicLocalizedText(template.nameAr)
  if (ar) return ar
  if (textContainsArabicScript(template.name)) return template.name
  const enKey = (template.nameEn ?? template.name).trim()
  return getOrderTemplateNameArOverride(template.id, enKey) ?? ''
}

export function getLocalizedName(item: WithLocalizedName, locale: Locale): string {
  if (locale === 'ar') {
    const ar = effectiveArabicLocalizedText(item.nameAr)
    if (ar) return ar
    if (textContainsArabicScript(item.name)) return item.name
    const enKey = (item.nameEn ?? item.name).trim()
    const override = getServiceTemplateNameArOverride(item.id ?? '', enKey)
    if (override) return override
    return enKey
  }
  if (locale === 'en' && item.nameEn) return item.nameEn
  return item.name
}

export function getLocalizedDescription(item: WithLocalizedDescription, locale: Locale): string {
  if (locale === 'ar') {
    const ar = effectiveArabicLocalizedText(item.descriptionAr)
    if (ar) return ar
    const raw = item.description?.trim() ?? ''
    if (raw && textContainsArabicScript(raw)) return item.description ?? ''
    const enKey = (item.descriptionEn ?? item.description ?? '').trim()
    const override = getServiceTemplateDescriptionArOverride(item.id ?? '', enKey)
    if (override) return override
    return (item.descriptionEn ?? item.description ?? '').trim()
  }
  if (locale === 'en' && item.descriptionEn) return item.descriptionEn
  return item.description ?? ''
}

export function getLocalizedAppointmentSpecLabel(spec: AppointmentSpec, locale: Locale): string {
  if (locale === 'ar') {
    const ar = effectiveArabicLocalizedText(spec.labelAr)
    if (ar) return ar
    if (textContainsArabicScript(spec.label)) return spec.label
    return getAppointmentSpecLabelArOverride(spec.id, spec.label) ?? spec.label
  }
  return spec.label
}

export function namesAlignServiceWithOrderTemplate(
  st: Pick<ServiceTemplate, 'name' | 'nameEn'>,
  orderTemplate: Pick<OrderTemplate, 'name' | 'nameEn'>
): boolean {
  const a = (st.nameEn ?? st.name ?? '').trim().toLowerCase()
  const b = (orderTemplate.nameEn ?? orderTemplate.name ?? '').trim().toLowerCase()
  return a.length > 0 && a === b
}

export function getIncludedServiceNameForAppointmentStep(
  st: ServiceTemplate | undefined,
  serviceSpecLabel: string,
  orderTemplate: OrderTemplate,
  locale: Locale,
  serviceCountInAppointment: number
): string {
  if (locale !== 'ar') {
    if (st) return getLocalizedName({ ...st, id: st.id }, locale)
    return serviceSpecLabel
  }
  if (st) {
    const n = getLocalizedName({ ...st, id: st.id }, 'ar')
    if (n.trim()) return n
  }
  if (textContainsArabicScript(serviceSpecLabel)) return serviceSpecLabel
  const templateAr = getOrderTemplateTitleSchedulingStep(orderTemplate, 'ar')
  if (!templateAr) return ''
  if (serviceCountInAppointment === 1) return templateAr
  if (st && namesAlignServiceWithOrderTemplate(st, orderTemplate)) return templateAr
  return ''
}

/**
 * Step 4 — description under each included service. Uses only nested `serviceTemplate` fields
 * (`description` / `descriptionAr` / `descriptionEn`); never `orderTemplate.description`.
 */
export function getIncludedServiceDescriptionForAppointmentStep(
  st: ServiceTemplate | undefined,
  locale: Locale
): string {
  if (!st) return ''
  if (locale !== 'ar') {
    const d = getLocalizedDescription({ ...st, id: st.id }, locale)
    if (d.trim()) return d
    return (st.description ?? st.descriptionEn ?? '').trim() || ''
  }
  const localizedAr = getLocalizedDescription({ ...st, id: st.id }, 'ar')
  if (localizedAr.trim()) return localizedAr
  const raw = (st.description ?? '').trim()
  if (raw && textContainsArabicScript(raw)) return st.description ?? ''
  if (raw) return st.description ?? ''
  const en = (st.descriptionEn ?? '').trim()
  if (en) return st.descriptionEn ?? ''
  const enKey = (st.descriptionEn ?? st.description ?? '').trim()
  return getServiceTemplateDescriptionArOverride(st.id, enKey) ?? ''
}
